import React from 'react'
import { useAuthTimeStamp } from '../../hooks/useAuthTimeStamp'
import { createFetcher } from '../../libs/next/api/jsonApiHandler'
import type { API } from '../../pages/api/user/[handler]'
import { LS_DOG_INFO_KEY } from '../components/dogForm/DogInfoForm'
import type { UserResult } from '../data/UserFragment'
import type { ForgotPasswordInput } from './ForgotPasswordInput'
import type { SignInInput } from './SignInInput'
import type { SignUpInput } from './SignUpInput'

export type Auth = { status: 'loading' } | { status: 'signedOut' } | { status: 'signedIn'; user: UserResult }

const api = createFetcher<API>('/api/user/[handler]')

export function useAuthContextRoot() {
	const [auth, setAuth] = React.useState<Auth>({ status: 'loading' })

	const [timeStamp] = useAuthTimeStamp()

	const reloadUser = React.useCallback(async () => {
		const me = await api('me', {})
		const user = me.me

		if (user) {
			setAuth({ status: 'signedIn', user })
		} else {
			setAuth({ status: 'signedOut' })
		}
	}, [])

	React.useEffect(() => {
		reloadUser()
	}, [reloadUser, timeStamp])

	return React.useMemo(
		() => ({
			...auth,
			forgottenPassword: (data: ForgotPasswordInput) => api('forgottenPassword', data),
			signIn: (data: SignInInput) =>
				api('signIn', data).then((res) => {
					if (res.user) {
						setAuth({ status: 'signedIn', user: res.user })
					} else {
						setAuth({ status: 'signedOut' })
					}
					return res
				}),
			signUp: (data: SignUpInput) =>
				api('signUp', data).then(async (res) => {
					await api('signIn', data).then((res) => {
						if (res.user) {
							setAuth({ status: 'signedIn', user: res.user })
						} else {
							setAuth({ status: 'signedOut' })
						}
						return res
					})
					return res
				}),
			signOut: () =>
				api('signOut', {}).then((res) => {
					setAuth({ status: 'signedOut' })
					window.localStorage.removeItem(LS_DOG_INFO_KEY)
					return res
				}),
			me: () =>
				api('me', {}).then((user) => {
					if (user.me) {
						setAuth({ status: 'signedIn', user: user.me })
					} else {
						setAuth({ status: 'signedOut' })
					}
					return user
				}),
		}),
		[auth],
	)
}

export type AuthContext = ReturnType<typeof useAuthContextRoot>

export const AuthContext = React.createContext<null | AuthContext>(null)

export function AuthContextProvider(props: { children: React.ReactNode }) {
	const value = useAuthContextRoot()
	return <AuthContext.Provider value={value}>{props.children}</AuthContext.Provider>
}

export function useAuth() {
	const context = React.useContext(AuthContext)

	if (!context) {
		throw new Error('Missing AuthContext')
	}

	return context
}

export function useIsSignedIn() {
	return useAuth().status === 'signedIn'
}

export function useIsSignedOut() {
	return useAuth().status === 'signedOut'
}
